import { combine, createEffect, createEvent, createStore } from 'effector';
import dayjs from 'dayjs';
import { $notificationStore } from '../notificationStore';
import { FileUtils, ApiUtils } from '@shared/utils';
import { ReportApi } from '@shared/api/rest';
import { IQueryParams, RequestStrategyEnum, type FileResponse } from '@shared/api/models';
import type {
  GenerateCardAccountBalanceReportRequest,
  GeneratePaymentAccountBalanceReportRequest,
  GeneratePaymentReportRequest,
  GenerateTransactionReportRequest,
} from '@shared/api/rest/report';

export const generatePaymentReportEffect = createEffect((params: IQueryParams<GeneratePaymentReportRequest>) =>
  ReportApi.generatePaymentReport(params.request, params?.signal),
);
export const generateCardAccountBalanceReport = createEffect((params: IQueryParams<GenerateCardAccountBalanceReportRequest>) =>
  ReportApi.generateCardAccountBalanceReport(params.request, params?.signal),
);
export const generatePaymentAccountBalanceReport = createEffect((params: IQueryParams<GeneratePaymentAccountBalanceReportRequest>) =>
  ReportApi.generatePaymentAccountBalanceReport(params.request, params?.signal),
);
export const generateTransactionReport = createEffect((params: IQueryParams<GenerateTransactionReportRequest>) =>
  ReportApi.generateTransactionReport(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<FileResponse | null>(null)
  .on(generatePaymentReportEffect.done, (_, data) => data.result)
  .on(generateCardAccountBalanceReport.done, (_, data) => data.result)
  .on(generatePaymentAccountBalanceReport.done, (_, data) => data.result)
  .on(generateTransactionReport.done, (_, data) => data.result)
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(generatePaymentReportEffect.pending, (_, data) => data)
  .on(generatePaymentReportEffect.fail, () => false)
  .on(generatePaymentReportEffect.done, () => false)
  .on(generateCardAccountBalanceReport.pending, (_, data) => data)
  .on(generateCardAccountBalanceReport.fail, () => false)
  .on(generateCardAccountBalanceReport.done, () => false)
  .on(generatePaymentAccountBalanceReport.pending, (_, data) => data)
  .on(generatePaymentAccountBalanceReport.fail, () => false)
  .on(generatePaymentAccountBalanceReport.done, () => false)
  .on(generateTransactionReport.pending, (_, data) => data)
  .on(generateTransactionReport.fail, () => false)
  .on(generateTransactionReport.done, () => false)
  .reset(clearEvent);

generatePaymentReportEffect.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Payments Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generatePaymentAccountBalanceReport.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Account Balance Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generateTransactionReport.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Transaction Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);
generateCardAccountBalanceReport.done.watch((result) =>
  FileUtils.downloadFile(result.result, `Account Balance Report - ${dayjs().format('YYYY-MM-DDTHH_mm_ss')}`),
);

generatePaymentReportEffect.fail.watch((x) => $notificationStore.events.setError(x.error.message));
generateCardAccountBalanceReport.fail.watch((x) => $notificationStore.events.setError(x.error.message));
generatePaymentAccountBalanceReport.fail.watch((x) => $notificationStore.events.setError(x.error.message));
generateTransactionReport.fail.watch((x) => $notificationStore.events.setError(x.error.message));

export const $reportStore = {
  events: {
    generatePaymentReport: ApiUtils.wrapperApi(generatePaymentReportEffect, RequestStrategyEnum.Debounce),
    generateCardAccountBalanceReport: ApiUtils.wrapperApi(generateCardAccountBalanceReport, RequestStrategyEnum.Debounce),
    generatePaymentAccountBalanceReport: ApiUtils.wrapperApi(generatePaymentAccountBalanceReport, RequestStrategyEnum.Debounce),
    generateTransactionReport: ApiUtils.wrapperApi(generateTransactionReport, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
  }),
};
